import { useCallback, useRef, useState } from 'react';
import { supabase } from '../lib/supabase.js';
import { validateRing, chunksToGeoJSON } from '../utils/drawGeometry.js';

// State machine for the custom-vineyard draw tool. Session shape:
//   { mode: 'draw'|'grab', stage: 'draw'|'details',
//     chunks: [{ id, points }], open: [[lat,lng],...],
//     error: string|null, fields: {}, saving, saveError }
// null = no active session. Nothing touches the DB until save().

let chunkSeq = 1;

export function useDrawVineyard({ onSaved }) {
  const [session, setSession] = useState(null);
  const sessionRef = useRef(null);
  sessionRef.current = session;

  const start = useCallback(() => {
    setSession({
      mode: 'draw', stage: 'draw',
      chunks: [], open: [],
      error: null, fields: {}, saving: false, saveError: null,
    });
  }, []);

  // Lasso "Make Multipolygon": open a session pre-filled with existing
  // geometry (one chunk per polygon's exterior ring), landing directly on
  // the details stage — the exact flow a hand-drawn shape reaches after
  // "Add details". The user can still go back and edit chunks. Interior
  // rings (holes) are dropped: the draw tool's chunks are single rings.
  const startFromGeometry = useCallback((geojson) => {
    const polys = geojson?.type === 'MultiPolygon' ? geojson.coordinates
                : geojson?.type === 'Polygon' ? [geojson.coordinates] : [];
    const chunks = polys.map((rings) => {
      const ring = (rings[0] ?? []).slice();
      if (ring.length > 1) {
        const [f, l] = [ring[0], ring[ring.length - 1]];
        if (f[0] === l[0] && f[1] === l[1]) ring.pop();   // drop closing dup
      }
      return { id: chunkSeq++, points: ring.map(([lng, lat]) => [lat, lng]) };
    }).filter(c => c.points.length >= 3);
    if (chunks.length === 0) return false;
    setSession({
      mode: 'draw', stage: 'details',
      chunks, open: [],
      error: null, fields: {}, saving: false, saveError: null,
    });
    return true;
  }, []);

  // Silent discard — for leaving the screen (mode change, logout), which
  // saves nothing and asks nothing.
  const discard = useCallback(() => setSession(null), []);

  // Cancel saves nothing. Confirms first when anything is drawn; returns
  // false when the user chose to keep the session.
  const cancel = useCallback(() => {
    const s = sessionRef.current;
    if (!s) return true;
    const n = s.chunks.length + (s.open.length > 0 ? 1 : 0);
    if (n > 0 && !window.confirm(`Discard ${n} shape${n === 1 ? '' : 's'}?`)) return false;
    setSession(null);
    return true;
  }, []);

  const setMode = useCallback((mode) => {
    // Switching draw/grab never discards anything — open points survive.
    setSession(s => s && { ...s, mode });
  }, []);

  const addPoint = useCallback((latlng) => {
    setSession(s => {
      if (!s || s.mode !== 'draw' || s.stage !== 'draw') return s;
      return { ...s, open: [...s.open, latlng], error: null };
    });
  }, []);

  // Undo the last placed point (repeatable back to zero).
  const undoPoint = useCallback(() => {
    setSession(s => {
      if (!s || s.stage !== 'draw' || s.open.length === 0) return s;
      return { ...s, open: s.open.slice(0, -1), error: null };
    });
  }, []);

  // Close the open ring into a chunk; on validation failure keep it open
  // and surface the message (never reject silently).
  const closeChunk = useCallback(() => {
    setSession(s => {
      if (!s || s.stage !== 'draw' || s.open.length === 0) return s;
      const err = validateRing(s.open);
      if (err) return { ...s, error: err };
      return {
        ...s,
        chunks: [...s.chunks, { id: chunkSeq++, points: s.open }],
        open: [],
        error: null,
      };
    });
  }, []);

  const deleteChunk = useCallback((id) => {
    setSession(s => s && { ...s, chunks: s.chunks.filter(c => c.id !== id) });
  }, []);

  // Stage switches keep geometry AND already-entered field values intact.
  const toDetails = useCallback(() => {
    setSession(s => (s && s.chunks.length > 0) ? { ...s, stage: 'details' } : s);
  }, []);
  const backToDraw = useCallback(() => {
    setSession(s => s && { ...s, stage: 'draw', mode: 'draw', saveError: null });
  }, []);

  const setField = useCallback((name, value) => {
    setSession(s => s && { ...s, fields: { ...s.fields, [name]: value } });
  }, []);

  const save = useCallback(async (patch, retireIds = []) => {
    const snapshot = sessionRef.current;
    if (!snapshot || snapshot.chunks.length === 0 || snapshot.saving) return null;
    setSession(s => s && { ...s, saving: true, saveError: null });
    try {
      const { data: id, error } = await supabase.rpc('create_custom_vineyard', {
        p_geom: chunksToGeoJSON(snapshot.chunks),
        p_patch: patch,
      });
      if (error) throw error;
      // migrate-data donors leave the stage once the new vineyard exists
      // (same semantic as the RLP register flow); best-effort, the new
      // vineyard is already saved either way
      for (const rid of retireIds) {
        // supabase builders are thenables without .catch — errors come back
        // in the result object, which we deliberately ignore here
        await supabase.rpc('update_vineyard', { p_id: rid, p_patch: { out_of_scope: true } });
      }
      setSession(null);
      onSaved?.(id);
      return id;
    } catch (e) {
      setSession(s => s && { ...s, saving: false, saveError: e.message });
      return null;
    }
  }, [onSaved]);

  return {
    session, start, startFromGeometry, cancel, discard, setMode,
    addPoint, undoPoint, closeChunk, deleteChunk,
    toDetails, backToDraw, setField, save,
  };
}
